import React, { useState } from 'react';
import { Cell, flexRender, Row, VisibilityState } from '@tanstack/react-table';
import { ColumnId } from '@sales-portal/domain/pricing/screens/customers/types';
import { createStyles } from '@/contexts/theme-context';
import {
  getCellAlignmentStyle,
  usePricingCellAlignmentStyles,
} from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-alignment-styles';
import { isLeftAlignedColumn } from '@sales-portal/domain/pricing/screens/customers/blocs/utils/table-ui';
import { Pressable } from 'react-native';
import { PricingCustomerListRow } from '@sales-portal/domain/pricing/screens/customers/blocs/transformers/use-customer-list-transformer';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { TableCell } from '@experiences/components/table/table-cell';
import { TableRow } from '@experiences/components/table/table-row';
import { useNavigation } from '@react-navigation/native';

const useStyles = createStyles((theme) => ({
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
}));

interface PricingCustomersTableRowProps {
  columnOrder?: string[];
  columnVisibility?: VisibilityState;
  index: number;
  row: Row<PricingCustomerListRow>;
}

export const PricingCustomersTableRow =
  React.memo<PricingCustomersTableRowProps>(({ index, row }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [styles] = useStyles();
    const [alignmentStyles] = usePricingCellAlignmentStyles();
    const navigation =
      useNavigation<StackNavigationProp<SalesPortalStackParams>>();

    const handleHoverIn = () => setIsHovered(true);
    const handleHoverOut = () => setIsHovered(false);

    const hoveredStyle = isHovered ? styles.hovered : undefined;

    const navigateToCustomerDetail = () =>
      navigation.navigate(SALES_PORTAL_ROUTES.PricingCustomerDetail, {
        restaurantUUID: row.original.restaurantUUID,
        supplierUUID: row.original.supplierUUID,
      });

    return (
      <Pressable
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        onPress={navigateToCustomerDetail}
        tabIndex={-1}
        testID={`pricing-customers-table-row-${row.index}`}
      >
        <TableRow index={index} key={row.id}>
          {row
            .getVisibleCells()
            .map((cell: Cell<PricingCustomerListRow, unknown>) => {
              const columnId = cell.column.id as ColumnId;
              const alignmentStyle = getCellAlignmentStyle({
                isDelta: false,
                isLeftAligned: isLeftAlignedColumn(columnId),
                styles: alignmentStyles,
              });
              return (
                <TableCell
                  cell={cell}
                  key={cell.id}
                  style={{ ...alignmentStyle, ...hoveredStyle }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              );
            })}
        </TableRow>
      </Pressable>
    );
  });
